import { generatePath } from 'react-router-dom';
import {
  Sidebar,
  SidebarBody,
  SidebarHeading,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from '@accure/ui';
import { HomeIcon } from '@heroicons/react/24/solid';

import { PageUrlKey } from '@/entities/page-url-key';
import { useDexVersion } from '@/hooks/use-dex-version';
import {
  type FeatureFlagChecker,
  useFeatureFlag,
} from '@/hooks/use-feature-flag';
import { useMe } from '@/hooks/use-me';
import { useWarrantyVersion } from '@/hooks/use-warranty-version';
import { cls } from '@/utils/cls';
import { getNavItemName } from '@/utils/get-nav-item-name';

import { AppSidebarFooter } from './app-sidebar-footer';
import { AppSidebarInternalIcon } from './app-sidebar-internal-icon';
import { AppSidebarItem } from './app-sidebar-item';
import { SiteSelection } from './site-selection';
import { VehicleSelection } from './vehicle-selection';

export type AppSidebarLocalProps = {
  className?: string;
  siteId: string;
};
export const AppSidebarLocal = ({
  className,
  siteId,
}: AppSidebarLocalProps) => {
  const me = useMe();
  const featureFlag = useFeatureFlag();
  const warrantyVersion = useWarrantyVersion();
  const dexVersion = useDexVersion();
  const topType = me.config.type === 'mobility' ? 'vehicle' : 'asset';

  const shouldShowAnalyzeSection = (featureFlagChecker: FeatureFlagChecker) => {
    return (
      featureFlagChecker('alerts') ||
      featureFlagChecker('taskmanager') ||
      featureFlagChecker('details') ||
      featureFlagChecker('pcs') ||
      featureFlagChecker('capacityTest') ||
      featureFlagChecker('warranty') ||
      featureFlagChecker('dataExplorer') ||
      featureFlagChecker('newDataExplorer') ||
      (featureFlagChecker('sitemap') && me.config.type !== 'mobility')
    );
  };

  const shouldShowOrganizeSection = (
    featureFlagChecker: FeatureFlagChecker,
  ) => {
    return (
      featureFlagChecker('projectManagement') ||
      (featureFlagChecker('records') && me.config.type !== 'mobility') ||
      featureFlagChecker('communication') ||
      featureFlagChecker('siteview')
    );
  };

  return (
    <div className="h-full">
      <Sidebar className={cls('w-48 border-r pl-[2px]', className)}>
        <SidebarBody>
          {featureFlag('landingpage') ? (
            <SidebarSection>
              <AppSidebarItem to="/">
                <HomeIcon />
                <SidebarLabel>Home</SidebarLabel>
                <AppSidebarInternalIcon featureFlag="landingpage" />
              </AppSidebarItem>
            </SidebarSection>
          ) : null}
          <SidebarSection>
            {me.config.type === 'mobility' ? (
              <VehicleSelection />
            ) : (
              <SiteSelection />
            )}
          </SidebarSection>
          <SidebarSection>
            {me.config.type !== 'mobility' ? (
              <AppSidebarItem
                to={generatePath(`/:topType/:siteId/${PageUrlKey.OVERVIEW}`, {
                  topType,
                  siteId,
                })}
              >
                {getNavItemName(PageUrlKey.OVERVIEW)}
              </AppSidebarItem>
            ) : null}
          </SidebarSection>
          {shouldShowAnalyzeSection(featureFlag) && (
            <SidebarSection>
              <SidebarHeading>Analyze</SidebarHeading>
              {featureFlag('alerts') ? (
                <AppSidebarItem
                  to={generatePath(`/:topType/:siteId/${PageUrlKey.ALERTS}`, {
                    topType,
                    siteId,
                  })}
                >
                  {getNavItemName(PageUrlKey.ALERTS)}
                  <AppSidebarInternalIcon featureFlag="alerts" />
                </AppSidebarItem>
              ) : null}

              {featureFlag('taskmanager') &&
              // TO-DO: Remove project management flag when ready to release
              featureFlag('projectManagement') ? (
                <AppSidebarItem
                  to={generatePath(`/:topType/:siteId/${PageUrlKey.TASKS}`, {
                    topType,
                    siteId,
                  })}
                >
                  {me.whiteLabel
                    ? 'Service Tasks'
                    : getNavItemName(PageUrlKey.TASKS)}
                  <AppSidebarInternalIcon featureFlag="taskmanager" />
                </AppSidebarItem>
              ) : null}

              {!me.whiteLabel && featureFlag('details') ? (
                <AppSidebarItem
                  to={generatePath(`/:topType/:siteId/${PageUrlKey.DETAILS}`, {
                    topType,
                    siteId,
                  })}
                >
                  {getNavItemName(PageUrlKey.DETAILS)}
                  <AppSidebarInternalIcon featureFlag="details" />
                </AppSidebarItem>
              ) : null}

              {featureFlag('pcs') ? (
                <AppSidebarItem
                  to={generatePath(`/:topType/:siteId/${PageUrlKey.PCS}`, {
                    topType,
                    siteId,
                  })}
                >
                  {getNavItemName(PageUrlKey.PCS)}
                  <AppSidebarInternalIcon featureFlag="pcs" />
                </AppSidebarItem>
              ) : null}

              {!me.whiteLabel && featureFlag('capacityTest') ? (
                <AppSidebarItem
                  to={generatePath(
                    `/:topType/:siteId/${PageUrlKey.CAPACITY_TEST}`,
                    {
                      topType,
                      siteId,
                    },
                  )}
                >
                  {getNavItemName(PageUrlKey.CAPACITY_TEST)}
                  <AppSidebarInternalIcon featureFlag="capacityTest" />
                </AppSidebarItem>
              ) : null}

              {!me.whiteLabel && warrantyVersion ? (
                <AppSidebarItem
                  to={generatePath(`/:topType/:siteId/${PageUrlKey.WARRANTY}`, {
                    topType,
                    siteId,
                  })}
                >
                  {getNavItemName(PageUrlKey.WARRANTY)}
                  <AppSidebarInternalIcon
                    featureFlag={warrantyVersion.featureFlag}
                    tooltip={warrantyVersion.internalTooltip}
                  />
                </AppSidebarItem>
              ) : null}

              {dexVersion ? (
                <AppSidebarItem
                  to={generatePath(
                    `/:topType/:siteId/${PageUrlKey.DATA_EXPLORER}`,
                    {
                      topType,
                      siteId,
                    },
                  )}
                >
                  {getNavItemName(PageUrlKey.DATA_EXPLORER)}
                  <AppSidebarInternalIcon
                    featureFlag={dexVersion.featureFlag}
                    tooltip={dexVersion.internalTooltip}
                  />
                </AppSidebarItem>
              ) : null}

              {featureFlag('sitemap') && me.config.type !== 'mobility' ? (
                <AppSidebarItem
                  to={generatePath(`/:topType/:siteId/${PageUrlKey.SITE_MAP}`, {
                    topType,
                    siteId,
                  })}
                >
                  {getNavItemName(PageUrlKey.SITE_MAP)}
                  <AppSidebarInternalIcon featureFlag="sitemap" />
                </AppSidebarItem>
              ) : null}

              {featureFlag('systemLogs') && me.config.type !== 'mobility' ? (
                <AppSidebarItem
                  to={generatePath(
                    `/:topType/:siteId/${PageUrlKey.SYSTEM_LOGS}`,
                    {
                      topType,
                      siteId,
                    },
                  )}
                >
                  {getNavItemName(PageUrlKey.SYSTEM_LOGS)}
                  <AppSidebarInternalIcon featureFlag="systemLogs" />
                </AppSidebarItem>
              ) : null}
            </SidebarSection>
          )}
          {shouldShowOrganizeSection(featureFlag) && (
            <SidebarSection>
              <SidebarHeading>Organize</SidebarHeading>
              {featureFlag('projectManagement') ? (
                <AppSidebarItem
                  to={generatePath(`/:topType/:siteId/${PageUrlKey.PROJECT}`, {
                    topType,
                    siteId,
                  })}
                >
                  {getNavItemName(PageUrlKey.PROJECT)}
                  <AppSidebarInternalIcon featureFlag="projectManagement" />
                </AppSidebarItem>
              ) : null}

              {featureFlag('records') && me.config.type !== 'mobility' ? (
                <AppSidebarItem
                  to={generatePath(
                    `/:topType/:siteId/${PageUrlKey.DOCUMENTS}`,
                    {
                      topType,
                      siteId,
                    },
                  )}
                >
                  {getNavItemName(PageUrlKey.DOCUMENTS)}
                  <AppSidebarInternalIcon featureFlag="records" />
                </AppSidebarItem>
              ) : null}
              {!me.whiteLabel && featureFlag('communication') ? (
                <AppSidebarItem
                  to={generatePath(
                    `/:topType/:siteId/${PageUrlKey.COMMUNICATION}`,
                    {
                      topType,
                      siteId,
                    },
                  )}
                >
                  {getNavItemName(PageUrlKey.COMMUNICATION)}
                  <AppSidebarInternalIcon featureFlag="communication" />
                </AppSidebarItem>
              ) : null}

              {featureFlag('siteview') ? (
                <AppSidebarItem
                  to={generatePath(`/:topType/:siteId/${PageUrlKey.SITEVIEW}`, {
                    topType,
                    siteId,
                  })}
                >
                  {getNavItemName(PageUrlKey.SITEVIEW)}
                  <AppSidebarInternalIcon featureFlag="siteview" />
                </AppSidebarItem>
              ) : null}
            </SidebarSection>
          )}

          <SidebarSpacer />
          <AppSidebarFooter />
        </SidebarBody>
      </Sidebar>
    </div>
  );
};
